/* eslint-disable @typescript-eslint/no-explicit-any */
import { NoteUserMetadata } from '@eigtech/notes-types'
import { Corn } from '@eigtech/shared-corn'
import { useCohortContext } from '@eigtech/ui-shared-api'
import { FormDrawer, createForm } from '@eigtech/ui-shared-forms/shadcn'
import { useCreateEntityNote, useNotesContext } from '@eigtech/ui-shared-notes'
import {
  Alert,
  AlertContent,
  AlertDescription,
  AlertIcon,
  AlertTitle,
} from '@eigtech/ui/components/alert'
import { DialogRootActions } from '@eigtech/ui/components/dialog'
import { withSheetErrorBoundary } from '@eigtech/ui/components/error-boundary/sheet'
import { SheetProps } from '@eigtech/ui/components/sheet'
import { toast } from '@eigtech/ui/components/sonner'
import { TriangleAlert } from 'lucide-react'
import { ReactNode, useImperativeHandle, useRef } from 'react'
import type { SetRequired } from 'type-fest'
import { makeAddNotePayload } from '../utils/makeAddNotePayload'
import { NoteFields } from './NoteFields'
import { NoteFieldsSchema, NoteFieldsType } from './NoteFieldsSchema'
import { NoteTemplateField } from './NoteTemplateField'
import { PublishNoteField } from './PublishNoteField'

export type CreateEntityNoteDrawerProps = SetRequired<
  Omit<SheetProps, 'handle'>,
  'open' | 'onOpenChange'
> & {
  entityCorn: Corn
  noteText?: string
  noteTitle?: string
  templateParams?: Record<string, unknown>
  title?: ReactNode
  includeTemplateField?: boolean
  includePublishField?: boolean
  userMetadata?: NoteUserMetadata
  onSuccess?: () => any
}

export const CreateEntityNoteDrawer = withSheetErrorBoundary(
  function CreateEntityNoteDrawer({
    actionsRef: propsActionsRef,
    entityCorn,
    noteText,
    noteTitle,
    templateParams,
    includePublishField = true,
    includeTemplateField = true,
    userMetadata,
    onSuccess,
    ...drawerProps
  }: CreateEntityNoteDrawerProps) {
    const actionsRef = useRef<DialogRootActions>(null)
    useImperativeHandle(propsActionsRef, () => actionsRef.current!)

    const { cohort } = useCohortContext()
    const { dataSource, metadata } = useNotesContext()

    const form = useForm({
      defaultValues: {
        title: noteTitle ?? '',
        text: noteText ?? '',
      },
    })

    const { mutateAsync: createNote, isError } = useCreateEntityNote(entityCorn)

    async function onSubmit(data: NoteFieldsType) {
      const payload = makeAddNotePayload({
        ...data,
        cohort,
        entityCorn,
        source: dataSource,
        templateParams,
      })

      await createNote({
        ...payload,
        userMetadata: {
          ...metadata,
          ...userMetadata,
        },
      })

      toast.success('Successfully created note!')

      onSuccess?.()
      actionsRef.current?.close()
    }

    return (
      <FormDrawer
        {...drawerProps}
        {...baseProps}
        actionsRef={actionsRef}
        form={form}
        onSubmit={onSubmit}
      >
        {includeTemplateField && <NoteTemplateField templateParams={templateParams} />}

        <NoteFields entityCorn={entityCorn} templateParams={templateParams} />

        {includePublishField && <PublishNoteField />}

        {isError && (
          <Alert variant="destructive">
            <AlertIcon>
              <TriangleAlert />
            </AlertIcon>
            <AlertContent>
              <AlertTitle>Could not create note</AlertTitle>
              <AlertDescription>
                Please try again. If the issue persists, please contact EIG Tech support.
              </AlertDescription>
            </AlertContent>
          </Alert>
        )}
      </FormDrawer>
    )
  },
  () => baseProps
)

const baseProps = { title: 'Create Note' }

const { useForm } = createForm(NoteFieldsSchema, 'createNoteForm')
